/**
 * The name a family has chosen, and what they add about the baby, saved as nameling:chosen. It stays
 * on the phone: it isn't shared with a partner or sent anywhere. Only "Choose a different name",
 * Settings → Start over or an import changes it.
 */
import { createStore, persist, readJson } from "./store";
import { native } from "./app";
import { count } from "./telemetry";
import { localDate } from "../data/format";

export interface Chosen {
  /** the chosen name's key (nameKey), or "" while nothing is chosen */
  key: string;
  /** the name as shown when it was chosen, so it still shows if it leaves the records */
  name: string;
  middle: string;
  /** when it was chosen (ms), 0 while nothing is */
  at: number;
  /** the due date as YYYY-MM-DD, or "" */
  due: string;
  /** a reminder on the due date (the iPhone app only) */
  remind: boolean;
  /** after the birth: the date (YYYY-MM-DD), the time (HH:MM) and the weight as typed */
  bornOn: string;
  bornAt: string;
  weight: string;
}

export const CHOSEN_KEY = "nameling:chosen";

const NOTHING: Chosen = { key: "", name: "", middle: "", at: 0, due: "", remind: false, bornOn: "", bornAt: "", weight: "" };

export const chosen = createStore<Chosen>(readJson<Chosen>(CHOSEN_KEY, NOTHING));
persist(chosen, CHOSEN_KEY);

export function choose(key: string, name: string): void {
  const was = chosen.get();
  if (was.key === key) return;
  chosen.set({ key, name, middle: "", at: Date.now() });
  count("chosen");
  syncReminder();
}

/** Back to choosing: the due date and birth details stay for the next name. */
export function unchoose(): void {
  chosen.set({ key: "", name: "", middle: "", at: 0 });
  count("chosen.undo");
  syncReminder();
}

/** Settings → Start over: everything goes, the reminder too. */
export function clearChosen(): void {
  chosen.set({ ...NOTHING });
  void syncReminder();
}

/** "Isla Rose Thompson" */
export function fullName(c: Chosen, surname: string): string {
  return [c.name, c.middle.trim(), surname.trim()].filter(Boolean).join(" ");
}

/** The reminder fires at 10 in the morning on the due date. */
export const REMIND_HOUR = 10;

export function reminderTime(due: string): Date | null {
  const d = localDate(due);
  if (!d) return null;
  d.setHours(REMIND_HOUR, 0, 0, 0);
  return d;
}

/**
 * Keep the iPhone's reminder in step with what's saved: set for the chosen name's due date while
 * it's wanted and still ahead, and cancelled otherwise.
 */
export async function syncReminder(): Promise<void> {
  const c = chosen.get();
  if (!native.reminder) return;
  const at = reminderTime(c.due);
  if (!c.remind || !c.key || !at || at.getTime() <= Date.now()) return native.reminder.cancel();
  await native.reminder.set({
    at,
    title: `${c.name}'s due date`,
    body: "Whenever you're ready, Nameling can make a card to announce the birth.",
    route: "#/list/chosen",
  });
}

/** Turn the reminder on (asking iOS for permission) or off. Resolves whether it's now on. */
export async function setReminder(on: boolean): Promise<boolean> {
  if (!native.reminder) return false;
  if (!on) {
    chosen.set({ remind: false });
    await native.reminder.cancel();
    return false;
  }
  if (!(await native.reminder.allow())) return false;
  chosen.set({ remind: true });
  await syncReminder();
  count("remind.set");
  return true;
}
